import React, { useEffect, useState, useCallback, useContext } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import AvailabilityManual from './AvailabilityManual';
import AvailabilitySettings from './AvailabilitySettings';
import AvailabilityIcalTab from './AvailabilityIcalTab';
import './Availability.css';
import { LanguageContext } from "../context/LanguageContext";

const API_URL = process.env.REACT_APP_API_URL;

export default function Availability() {
  const { objectId } = useParams();
  const navigate = useNavigate();
  const { texts } = useContext(LanguageContext);

  const [activeTab, setActiveTab] = useState('settings');
  const [availabilityMode, setAvailabilityMode] = useState("none");
  const [availabilityStatus, setAvailabilityStatus] = useState({});
  const [settingsLoaded, setSettingsLoaded] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState('');

  // ⭐ Objekt-Switcher wie bei Preisen
  const [ownerObjects, setOwnerObjects] = useState([]);
  const [objectName, setObjectName] = useState("");

  // ------------------------------------------------------------
  // Verfügbarkeitsmodus laden
  // ------------------------------------------------------------
    const fetchMode = useCallback(async () => {
      try {
        const res = await fetch(
          `${process.env.REACT_APP_API_URL}/api/availability/settings/${objectId}`,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("authToken")}`,
            },
          }
        );

        if (!res.ok) {
          console.error("fetchMode HTTP error", res.status);
          return;
        }

        const data = await res.json();

        setAvailabilityMode(data.availability_mode); // "none" | "ical" | "manual"
        setSettingsLoaded(true);
      } catch (err) {
        console.error("fetchMode error", err);
      }
    }, [objectId]);


  // ------------------------------------------------------------
  // Verfügbarkeiten laden
  // ------------------------------------------------------------
  const fetchAvailability = useCallback(async () => {
    if (!objectId) return;
    setIsLoading(true);

    try {
      const res = await fetch(`${API_URL}/object/${objectId}/availability`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('authToken')}` },
      });

      if (!res.ok) throw new Error('Fetch failed');

      const data = await res.json();
      const mapped = {};

      data.forEach(({ start_date, end_date, id }) => {
        let cur = new Date(start_date);
        const end = new Date(end_date);

        while (cur <= end) {
          const key = cur.toISOString().slice(0, 10);
          mapped[key] = { status: 'available', id };
          cur.setDate(cur.getDate() + 1);
        }
      });

      setAvailabilityStatus(mapped);
    } catch (err) {
      console.error('fetchAvailability error', err);
      setMessage(texts.availability.errorLoading);
      setTimeout(() => setMessage(''), 3000);
    } finally {
      setIsLoading(false);
    }
  }, [objectId, texts]);

  // ------------------------------------------------------------
  // Objektliste laden (wie bei Preisen)
  // ------------------------------------------------------------
  const fetchObjectData = useCallback(async () => {
    const token = localStorage.getItem("authToken");
    if (!token) {
      setObjectName(`${texts.availability.object} ${objectId}`);
      return;
    }

    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/objects`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!response.ok) throw new Error(texts.availability.errorFetchObjects);

      const objects = await response.json();

      const matchedObject = objects.find(
        (obj) => String(obj.id) === String(objectId)
      );

      setObjectName(
        matchedObject?.name || `${texts.availability.object} ${objectId}`
      );

      if (matchedObject) {
        const sameOwner = objects.filter(
          (obj) => obj.owner_id === matchedObject.owner_id
        );
        setOwnerObjects(sameOwner);
      } else {
        setOwnerObjects(objects);
      }

    } catch (error) {
      console.error(texts.availability.errorFetchObjectName, error);
      setObjectName(`${texts.availability.object} ${objectId}`);
    }
  }, [objectId, texts]);

  // ------------------------------------------------------------
  // Automatische Weiterleitung, sobald Modus geladen ist
  // ------------------------------------------------------------
  useEffect(() => {
    if (!settingsLoaded) return;

    if (availabilityMode === "ical") {
      setActiveTab("ical");
    }

    if (availabilityMode === "manual") {
      setActiveTab("manual");
    }

    if (availabilityMode === "none") {
      setActiveTab("settings");
    }
  }, [availabilityMode, settingsLoaded]);

  // ------------------------------------------------------------
  // Initial laden
  // ------------------------------------------------------------
  useEffect(() => {
    fetchMode();
    fetchAvailability();
    fetchObjectData(); // ⭐ neu
  }, [fetchMode, fetchAvailability, fetchObjectData]);

  // ------------------------------------------------------------
// Objektwechsel erkennen → Tabs zurücksetzen
// ------------------------------------------------------------
  useEffect(() => {
    setActiveTab("settings");
    setSettingsLoaded(false);
  }, [objectId]);

  // ------------------------------------------------------------
  // Tabs abhängig vom Modus filtern
  // ------------------------------------------------------------
  const showIcalTab = availabilityMode === "ical";
  const showManualTab = availabilityMode === "manual";

  return (
    <div className="price-availability-container">

      {/* ---------------- TOP NAVIGATION ---------------- */}
      <div className="tabs-row">
        <div className="tabs-left" role="tablist" aria-label={texts.availability.tabsAriaLabel}>

          <button
            type="button"
            className={`tab-button ${activeTab === 'settings' ? 'active' : ''}`}
            onClick={() => setActiveTab('settings')}
          >
            {texts.availability.tabSettings}
          </button>

          {showIcalTab && (
            <button
              type="button"
              className={`tab-button ${activeTab === 'ical' ? 'active' : ''}`}
              onClick={() => setActiveTab('ical')}
            >
              {texts.availability.tabIcal}
            </button>
          )}

          {showManualTab && (
            <button
              type="button"
              className={`tab-button ${activeTab === 'manual' ? 'active' : ''}`}
              onClick={() => setActiveTab('manual')}
            >
              {texts.availability.tabManual}
            </button>
          )}
        </div>

        {/* ---------------- RIGHT SIDE BUTTONS ---------------- */}
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 8, alignItems: 'center' }}>

          {/* ⭐ Objekt-Switcher wie bei Preisen */}
          {ownerObjects.length > 1 && (
            <select
              value={objectId}
              onChange={(e) => navigate(`/availability/${e.target.value}`)}
              className="object-switcher"
            >
              {ownerObjects.map(obj => (
                <option key={obj.id} value={obj.id}>
                  {obj.name || `${texts.availability.object} ${obj.id}`}
                </option>
              ))}
            </select>
          )}

          <button
            type="button"
            className="tab-button"
            onClick={() => navigate(`/prices/${objectId}`)}
          >
            {texts.availability.tabPrices}
          </button>

          <button
            type="button"
            className="tab-button"
            onClick={() => navigate('/object-list')}
          >
            {texts.availability.tabObjectList}
          </button>
        </div>
      </div>

      {/* ---------------- MESSAGE ---------------- */}
      {message && <div className="message">{message}</div>}

      {/* ---------------- TAB CONTENT ---------------- */}
      <div className="tab-content">
        {activeTab === 'settings' && (
          <AvailabilitySettings
            objectId={objectId}
            onRefresh={() => {
              fetchMode();
              fetchAvailability();
            }}
            setMessage={setMessage}
            setActiveTab={setActiveTab}
            availabilityMode={availabilityMode}
            setAvailabilityMode={setAvailabilityMode}
          />
        )}

        {activeTab === 'ical' && showIcalTab && (
          <AvailabilityIcalTab
            objectId={objectId}
            onRefresh={fetchAvailability}
            setMessage={setMessage}
          />
        )}

        {activeTab === 'manual' && showManualTab && (
          <AvailabilityManual
            objectId={objectId}
            availabilityStatus={availabilityStatus}
            setAvailabilityStatus={setAvailabilityStatus}
            refreshFromServer={fetchAvailability}
            isLoading={isLoading}
          />
        )}
      </div>
    </div>
  );
}
